import type { RpcError } from 'grpc-web';
import { DataExport, DeleteAccountRequest, DeletionStatus, UserData } from '../generated/user_pb';
import { UsernatorClient } from '../generated/user_grpc_web_pb';
import GRPC_BACKEND_URL, {GRPC_CLIENT_OPTIONS, SSO_ENABLED} from '../grpcConfig';
import { tryParseUserDataFromLocalStorage } from '../utils/userStorage';

const client = new UsernatorClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS);
type Callback<T> = (error: RpcError, response: T) => void;

function call<T>(invoke: (user: UserData, callback: Callback<T>) => void): Promise<T> {
    const user = tryParseUserDataFromLocalStorage(true);
    if (!user || (!user.getToken() && !SSO_ENABLED)) return Promise.reject(new Error('No signed-in user'));

    return new Promise((resolve, reject) => invoke(user, (error, response) => {
        if (error) reject(error);
        else if (response) resolve(response);
        else reject(new Error('The user service returned no response'));
    }));
}

export function getDeletionStatus(): Promise<string | null> {
    return call<DeletionStatus>((user, callback) => client.getDeletionStatus(user, undefined, callback))
        .then(status => status.getScheduledfor() || null);
}

export function requestAccountDeletion(password: string): Promise<void> {
    return call<DeletionStatus>((user, callback) => {
        const request = new DeleteAccountRequest();
        request.setUser(user);
        request.setPassword(password);
        client.requestAccountDeletion(request, undefined, callback);
    }).then(() => undefined);
}

export function cancelAccountDeletion(): Promise<void> {
    return call<DeletionStatus>((user, callback) => client.cancelAccountDeletion(user, undefined, callback))
        .then(() => undefined);
}

export function exportMyData(): Promise<{ fileName: string; json: string }> {
    return call<DataExport>((user, callback) => client.exportMyData(user, undefined, callback))
        .then(data => ({ fileName: data.getFilename(), json: data.getJson() }));
}
